<template>
  <UserSelectorWrapper
    v-if="!!event?.id"
    :selected-users="assignedPeople"
    :selected-material-resources="assignedMaterialResources"
    :filter="filterUser"
    :edition-mode="$can('manage_planning')"
    :avatar-size="avatarSize"
    :event="event"
    :all-users-availability-items="allUsersAvailabilityItems"
    :all-material-resources-availability-items="
      allMaterialResourcesAvailabilityItems
    "
    @modal-open="fetchAssignedAssets"
    @add-material-resource="addMaterialResource"
    @remove-material-resource="removeMaterialResource"
  >
    <template #top-left-cta>
      <SelectComponent
        v-model="selectedRole"
        name="roles"
        :values="roles"
        multiple
      />
    </template>
    <template #selected-user-panel="{ user }">
      <SelectedEventUser
        :user="user"
        :event-id="event.id"
        :edition-mode="$can('manage_planning')"
        :all-users-availability-items="allUsersAvailabilityItems"
        @updated="fetchAssignedAssets"
      />
    </template>
    <template #selectable-user-panel="{ user }">
      <SelectableEventUser
        :user="user"
        :event="event"
        :edition-mode="$can('manage_planning')"
        :occupations="
          occupations.find((occupation) => occupation.id === user.id)
            ?.assigned_event ?? []
        "
        :all-users-availability-items="allUsersAvailabilityItems"
        @add-user="addUser"
      />
    </template>
  </UserSelectorWrapper>
</template>

<script>
import { defineComponent } from 'vue'
import UserSelectorWrapper from '@c/BaseSelectorComponent/UserSelectorModal/UserSelectorWrapper.vue'
import { mapActions, mapGetters } from 'vuex'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import SelectedEventUser from '@c/Event/SelectedEventUser.vue'
import SelectableEventUser from '@c/Event/SelectableEventUser.vue'

export default defineComponent({
  name: 'EventUserSelector',
  components: {
    SelectableEventUser,
    SelectedEventUser,
    SelectComponent,
    UserSelectorWrapper
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    avatarSize: {
      type: String,
      required: false,
      default: 'small'
    }
  },
  emits: ['update:event'],
  data() {
    return {
      selectedRole: [],
      assignedPeople: [],
      assignedMaterialResources: [],
      occupations: [],
      allUsersAvailabilityItems: [],
      allMaterialResourcesAvailabilityItems: []
    }
  },
  computed: {
    ...mapGetters({
      roles: 'metadata/getRolesMetadata'
    })
  },
  watch: {
    'event.assignedPeopleCollection': {
      immediate: true,
      handler(newVal) {
        const val = newVal ?? []
        if (!this.hasSameAssignments(val, this.assignedPeople)) {
          this.assignedPeople = val
        }
      }
    },
    'event.materialResourcesCollection': {
      immediate: true,
      handler(newVal) {
        const val = newVal ?? []
        if (!this.hasSameAssignments(val, this.assignedMaterialResources)) {
          this.assignedMaterialResources = val
        }
      }
    }
  },
  methods: {
    ...mapActions({
      processGetAssignedPersonAction: 'planning/getAssignedPerson',
      processGetAssignedAssetsAction: 'planning/getAssignedAssets',
      processAddAssignedPersonAction: 'planning/addAssignedPerson',
      processAddAssignedMaterialResourceAction:
        'planning/addAssignedMaterialResource',
      processRemoveAssignedMaterialResource:
        'planning/removeAssignedMaterialResource'
    }),
    addUser(data) {
      this.processAddAssignedPersonAction({
        event_id: this.event.id,
        ...data
      })
        .then(() => {
          this.fetchAssignedAssets()
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
    },
    addMaterialResource(material_resource_id) {
      this.processAddAssignedMaterialResourceAction({
        event_id: this.event.id,
        material_resource_id: material_resource_id
      })
        .then(() => {
          this.fetchAssignedAssets()
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
    },
    removeMaterialResource(material_resource_id) {
      this.processRemoveAssignedMaterialResource({
        event_id: this.event.id,
        material_resource_id: material_resource_id
      })
        .then(() => {
          this.fetchAssignedAssets()
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
    },
    // Vérifie si deux listes d'assignations produiraient le même affichage
    // d'avatars. On compare uniquement id + champs de disponibilité (statut,
    // fonction) car ce sont les seuls champs qui pilotent le rendu visuel des
    // avatars. Les autres propriétés (nom, etc.) n'affectent pas l'affichage
    // ici et ne doivent pas déclencher d'emit inutile vers le store.
    hasSameAssignments(a, b) {
      if (a.length !== b.length) return false
      const key = (x) =>
        `${x.disponibility?.assignement_status ?? null}|${x.disponibility?.function ?? null}`
      const map = new Map(a.map((x) => [x.id, key(x)]))
      return b.every((x) => map.get(x.id) === key(x))
    },
    filterUser(user) {
      return (
        this.selectedRole.length === 0 ||
        user.roles.filter((role) => this.selectedRole.includes(role)).length > 0
      )
    },
    fetchAssignedAssets() {
      this.processGetAssignedAssetsAction({
        event_id: this.event.id
      }).then((response) => {
        this.occupations = response.occupations
        this.allUsersAvailabilityItems = response.users_availability_items
        this.allMaterialResourcesAvailabilityItems =
          response.material_resources_availability_items
        const assignedPeople = response.assigned_people ?? []
        const assignedMaterialResources = response.assigned_material_resources ?? []
        const changed =
          !this.hasSameAssignments(assignedPeople, this.assignedPeople) ||
          !this.hasSameAssignments(assignedMaterialResources, this.assignedMaterialResources)
        // Mise à jour immédiate pour l'affichage local (avatars).
        // Le watcher sur event.assignedPeopleCollection / materialResourcesCollection
        // prend le relais une fois que le parent a reçu l'emit ci-dessous.
        this.assignedPeople = assignedPeople
        this.assignedMaterialResources = assignedMaterialResources
        if (!changed) return
        // Remonte l'event mis à jour pour synchroniser les collections dans le store
        // avant toute sauvegarde du dossier. Sans cet emit, le parent conservait
        // les anciennes valeurs et les écrasait côté serveur lors du save.
        this.$emit('update:event', {
          ...this.event,
          assignedPeopleCollection: assignedPeople,
          assignedPeople: assignedPeople.map((u) => u.id),
          materialResourcesCollection: assignedMaterialResources,
          materialResources: assignedMaterialResources.map((r) => r.id)
        })
      })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
    }
  }
})
</script>
